<template>
    <div class="item-question-box-check">
        <div class="d-flex flex-row">
            <label v-if="numberContent != ''" class="text-color-number mb-1">
                {{ numberContent }}
            </label>
            <label class="text-color-questions mb-1 ps-2"
                   v-html="labelContent">
            </label>
        </div>
        <div
            v-if="question.additionalInfo != ''"
            class="form-input-help mb-1"
            v-html="question.additionalInfo"
        >
        </div>
        <check-input :error-text="translate('label.this_value_is_required')"
                     :field-id="question.questionId"
                     :input-class="'form-check-input question-input-check'"
                     :name="fieldName"
                     :options="options"
                     :other-id="otherId"
                     :other-text="translateLabels.textOther"
                     :question="question"
                     :type="'check'"
                     :type-field-id="TYPE_FIELD_ID"
                     :validation="validationRules"
                     :has-submitted="hasSubmitted"
                     @update:modelAnswer="changeValue($event, question)"
                     @register-field="handleFieldRegister"
                     @field-change="handleFieldChange"
                     @other-selected="handleOtherSelected"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { usePlayerStore } from '@/playerVue3/storage/storePlayer';
import CheckInput from "../components/check-input.vue";
import { useTranslation } from '@/composables/useTranslation';

// INITIALIZE STORE AND COMPOSABLES
const playerStore = usePlayerStore();
const { translate } = useTranslation();

// PROPS AND EMITS
const props = defineProps({
    enumeration: {
        type: Number,
        required: false,
        default: 0,
    },
    question: {
        type: Object,
        required: true,
    },
    showNumbering: {
        type: Boolean,
        required: false,
        default: false,
    },
    translateLabels: {
        type: Object,
        required: true,
    },
    TYPE_FIELD_ID: {
        type: Number,
        required: true,
    },
    hasSubmitted: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['field-change', 'register-field', 'other-field-data']);

// REACTIVE VARIABLES
const otherId = ref(0);

// COMPUTED PROPERTIES
const fieldName = computed(() =>
    `question_${props.TYPE_FIELD_ID}_${props.question.questionId}_${props.question.fieldType}`
);

const labelContent = computed(() => {
    const label = [];

    if (Boolean(props.question.required)) {
        label.push(`* ${props.question.description}`);
    } else {
        label.push(props.question.description);
    }

    return label.join(' ');
});

const numberContent = computed(() => {
    const label = [];

    if (Boolean(props.showNumbering)) {
        label.push(`${String(props.enumeration).padStart(2, '0')}.`);
    }

    return label.join(' ');
});

const options = computed(() => {
    let options = {};
    let textOther = props.translateLabels.textOther;
    props.question.options.forEach(function (element) {
        let value = element.optionId;
        let label = String(element.description);
        if (element.other === true) {
            otherId.value = parseInt(value);
            value = 'other';
            label = textOther;
        }
        options[value] = {
            'label': label,
            'additionalInfo': element.additionalInfo || '',
        }
    });
    return options;
});

const validationRules = computed(() => {
    const rules = {
        required: false,
    };
    if (props.question.required) {
        rules.required = true;
    }

    return rules;
});

// METHODS
const changeValue = (newValue, qt) => {
    qt.answer = newValue;
    emit('field-change', fieldName.value, newValue);
    playerStore.setAnswerInFormQuestions(qt);
};

const handleFieldRegister = (fieldName, initialValue) => {
    emit('register-field', fieldName, initialValue);
};

const handleFieldChange = (fieldName, value) => {
    emit('field-change', fieldName, value);
};

const handleOtherSelected = (data) => {
    const qt = props.question;
    qt.answerOther = data.otherValue;
    playerStore.setAnswerOtherInFormQuestions(qt);
    emit('other-field-data', {
        quetionType: props.TYPE_FIELD_ID,
        questionId: props.question.questionId,
        optionId: data.optionId,
        otherValue: data.otherValue,
        isSelected: data.isSelected
    });
};

const checkPreloadedOtherData = () => {
    if (props.question.answer && Array.isArray(props.question.answer) && props.question.answerOther) {
        // Verificar si "other" está seleccionado en las respuestas
        const hasOtherSelected = props.question.answer.includes('other') ||
            props.question.answer.includes(otherId.value.toString());

        if (hasOtherSelected && otherId.value > 0) {
            // Emitir los datos del campo "other" precargado
            handleOtherSelected({
                optionId: otherId.value,
                otherValue: props.question.answerOther,
                isSelected: true
            });
        }
    }
};

// LIFECYCLE HOOKS
onMounted(async () => {
    const initialValue = props.question.answer || [];
    emit('register-field', fieldName.value, initialValue);

    // Verificar datos "other" precargados
    checkPreloadedOtherData();
});
</script>
